import { loadRemoteModule } from "@cw-elements/mfe-setup/load";
import { type TopbarNavigationMFEProps } from "@cw-elements/host-kit";
import React, {
  lazy,
  Suspense,
  useContext,
  useMemo,
  useSyncExternalStore,
  type FC,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "lib/auth/AuthService";
import Logo from "assets/images/logo.svg";
import "./header.less";
import { GlobalContext } from "context/AppContextProvider";
import { isEmpty } from "lodash";
import {
  getMyCropwiseLocation,
  scrollPageToTop,
  subscribeMyCropwiseHistory,
} from "shared/utils/myCropwiseHistory";
import {
  MY_CROPWISE_ROOT,
  myCropwiseOrganizationPath,
  myCropwiseWorkspacePath,
} from "shared/utils/productEntry";
import {
  getIdAfterSegment,
  getWorkspaceId,
  isWorkspaceId,
  withWorkspaceId,
} from "shared/utils/workspaceRouting";

const APP_NAME = "Cropwise Carbon Engine Portal";

const TopbarNavigation = lazy(
  async () =>
    await loadRemoteModule<TopbarNavigationMFEProps>("topbar-navigation/index")
);

export const GlobalHeader: FC = (): ReactElement => {
  const hostLocation = useLocation();
  const hallLocation = useSyncExternalStore(
    subscribeMyCropwiseHistory,
    getMyCropwiseLocation
  );
  const inMyCropwise =
    hostLocation.pathname === MY_CROPWISE_ROOT ||
    hostLocation.pathname.startsWith(`${MY_CROPWISE_ROOT}/`);
  const hallHasRoute =
    hallLocation.pathname === MY_CROPWISE_ROOT ||
    hallLocation.pathname.startsWith(`${MY_CROPWISE_ROOT}/`);
  const { pathname, search } =
    inMyCropwise && hallHasRoute ? hallLocation : hostLocation;
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { orgState } = useContext(GlobalContext);

  const topNavigationHeaderProps = useMemo(() => {
    let newWorkspaceId = "";
    let newOrgId = "";
    if (
      !inMyCropwise &&
      !isEmpty(orgState?.currentOrg) &&
      orgState?.currentOrg?.id &&
      orgState?.currentOrg?.workspace_id
    ) {
      newWorkspaceId = orgState?.currentOrg?.workspace_id;
      newOrgId = orgState?.currentOrg?.id;
    }

    // The address wins over org state: it is what decides which organization is
    // on screen, and it is already correct on the first render, before the org
    // has been fetched. Whichever id the address does not carry keeps the value
    // from org state.
    const orgIdFromPath = getIdAfterSegment(pathname, "organization");
    const workspaceIdFromPath = getWorkspaceId(pathname, search);
    if (orgIdFromPath) {
      newOrgId = orgIdFromPath;
    }
    if (workspaceIdFromPath) {
      newWorkspaceId = workspaceIdFromPath;
    } else if (new URLSearchParams(search).has("workspaceId")) {
      newWorkspaceId = "";
    }

    // Built after the address is read, not before: this is what the settings
    // menu is handed, and a snapshot taken earlier left it with the empty ids
    // that cost it its gear.
    const output = {
      workspaceId: newWorkspaceId,
      orgId: newOrgId,
    };

    const handleLanguageError = (error: unknown): any => {
      console.error("Failed to change language:", error);
    };

    const isEntitiHidden = !newWorkspaceId;

    const outputObj = {
      selectedOrganization: {
        value: newOrgId || "",
      },
      selectedWorkspace: {
        value: newWorkspaceId || "",
      },
    };

    const openInMyCropwise = (path: string): void => {
      scrollPageToTop();
      navigate(path, {
        state: {
          stopRedirect: true,
        },
      });
    };

    const workspaceForShortcut = (workspaceId?: string): string => {
      return isWorkspaceId(workspaceId) ? workspaceId : newWorkspaceId;
    };

    return {
      userLanguage: i18n.language,
      productImage: {
        redirectUrl: "/",
        imageSrc: Logo,
        name: "",
      },

      workspaceDropdown: () => ({
        goToEntity: () => {
          openInMyCropwise(MY_CROPWISE_ROOT);
        },
      }),

      settingsMenuConfig: () => output,
      featureConfig: {
        hasSettingsMenu: true,
        hasProfileDropdown: true,
        hasDeckOfApps: true,
        hideEntitiesDropdown: isEntitiHidden,
        hasAiChatbot: true,
      },
      onLogout: () => logout(),
      profileDropdownConfig: () => ({
        availableUnitSystems: [], // set [] to remove unit system option from profie dropdown.
        onLanguageChange: (language: string) => {
          void i18n.changeLanguage(language).catch(handleLanguageError);
        },
      }),
      defaultItems: outputObj, // this is IMP to show current group name in the organization dropdown in top header.
      // Kept in sync after the first render so Deck of Apps can append
      // ?workspace=&organization= instead of dropping the user on a picker.
      currentPageContext: {
        workspaceId: isWorkspaceId(newWorkspaceId) ? newWorkspaceId : "",
        orgId: newOrgId,
      },
      // The "Open in My Cropwise" shortcut renders only when workspace,
      // organization, and farm goToEntity all exist. The farm list stays
      // hidden; its callback is the one used once an organization is selected.
      farmDropdown: () => ({
        hide: true,
        goToEntity: (entities?: { workspaceId?: string; orgId?: string }) => {
          openInMyCropwise(
            myCropwiseOrganizationPath(
              workspaceForShortcut(entities?.workspaceId),
              entities?.orgId ?? newOrgId
            )
          );
        },
      }),
      organizationDropdown: () => ({
        onSelect: (org: { value: string; workspaceId?: string }) => {
          if (!isWorkspaceId(org.workspaceId)) {
            console.error(
              "Organization selection is missing a valid workspace."
            );
            navigate(MY_CROPWISE_ROOT);
            return;
          }
          navigate(
            withWorkspaceId(
              `/app/organization/${org.value}/assessments`,
              org.workspaceId
            )
          );
        },
        goToEntity: (entities?: { workspaceId?: string }) => {
          const workspaceId = workspaceForShortcut(entities?.workspaceId);
          openInMyCropwise(myCropwiseWorkspacePath(workspaceId));
        },
      }),
    };
  }, [pathname, search, inMyCropwise, orgState, navigate, i18n]);

  return (
    <Suspense fallback={<div style={{ height: "50px" }}>&nbsp;</div>}>
      <TopbarNavigation
        {...topNavigationHeaderProps}
        appData={{
          name: APP_NAME,
          id: process.env.REACT_APP_ID,
        }}
      />
    </Suspense>
  );
};
